import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import config from '../../config/config';

export function ViewIncome() {
    const [incomeData, setIncomeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const token = localStorage.getItem('token');

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const selectedYear = localStorage.getItem('qurbani_year') || new Date().getFullYear();
                setLoading(true);
                const response = await fetch(`${config.baseUrl}/api/otherInc/getOtherInc?year=${selectedYear}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data && Array.isArray(data.data)) setIncomeData(data.data);
                else if (Array.isArray(data)) setIncomeData(data);
                else setIncomeData([]);
                setError(null);
            } catch (error) {
                setIncomeData([]);
                setError("Failed to load data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredData = incomeData.filter(item => {
        const search = searchTerm.toLowerCase();
        return (
            item.item_name?.toLowerCase().includes(search) ||
            item.person_name?.toLowerCase().includes(search) ||
            item.total_amount?.toString().includes(search)
        );
    });

    const lastIndex = currentPage * entriesPerPage;
    const firstIndex = lastIndex - entriesPerPage;
    const currentItems = filteredData.slice(firstIndex, lastIndex);
    const totalPages = Math.ceil(filteredData.length / entriesPerPage);

    if (loading) return (
        <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
    );

    return (
        <main className="space-y-3">

            {/* Header Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                            <svg width="16" height="16" fill="none" stroke="#4338ca" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-sm font-black text-slate-800 uppercase tracking-tight">Other Income</h1>
                            <p className="text-xs text-slate-400">{incomeData.length} total records</p>
                        </div>
                    </div>
                    <Link
                        to="/other-income/add"
                        className="flex items-center gap-1.5 px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-black rounded-lg uppercase tracking-wider transition-all active:scale-95"
                    >
                        <span className="text-base leading-none">+</span> Add Income
                    </Link>
                </div>

                {/* Search & Filter Bar */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex flex-col md:flex-row justify-between items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Show</span>
                        <select
                            className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-400"
                            value={entriesPerPage}
                            onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">entries</span>
                    </div>

                    <div className="relative w-full md:w-72">
                        <input
                            type="text"
                            placeholder="Search income..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
                        />
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-indigo-700 text-indigo-100 text-[10px] font-black uppercase tracking-wider">
                                <th className="px-5 py-3.5">#</th>
                                <th className="px-5 py-3.5">Type</th>
                                <th className="px-5 py-3.5">Person</th>
                                <th className="px-5 py-3.5">Qty / Rate</th>
                                <th className="px-5 py-3.5">Total</th>
                                <th className="px-5 py-3.5">Date</th>
                                <th className="px-5 py-3.5 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                            {currentItems.length > 0 ? (
                                currentItems.map((income, index) => (
                                    <tr key={index} className="hover:bg-indigo-50/40 transition-colors">
                                        <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{firstIndex + index + 1}</td>
                                        <td className="px-5 py-3.5">
                                            <span className="px-2.5 py-1 bg-indigo-100 text-indigo-800 text-xs font-black rounded-full">
                                                {income.item_name}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3.5 text-slate-700 text-xs font-bold">{income.person_name}</td>
                                        <td className="px-5 py-3.5">
                                            <div className="text-slate-800 text-xs font-bold">{income.qty} units</div>
                                            <div className="text-[10px] text-slate-400 uppercase font-bold">@ Rs. {income.rate}</div>
                                        </td>
                                        <td className="px-5 py-3.5 font-black text-indigo-700 font-mono text-sm">
                                            Rs. {income.total_amount}
                                        </td>
                                        <td className="px-5 py-3.5 text-slate-400 text-xs">
                                            {income.create_at ? new Date(income.create_at).toLocaleDateString('en-GB') : 'N/A'}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <div className="flex justify-center gap-3">
                                                <Link to={`/other-income/edit/${income.o_id}`} className="text-indigo-500 hover:text-indigo-800 transition-all">
                                                    <FaEdit size={14} />
                                                </Link>
                                                <button className="text-red-400 hover:text-red-700 transition-all">
                                                    <FaTrash size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400 text-xs italic">
                                        No income records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {filteredData.length > 0 && (
                    <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-3">
                        <span className="text-[11px] text-slate-400 font-bold">
                            Showing {firstIndex + 1}–{Math.min(lastIndex, filteredData.length)} of {filteredData.length} entries
                        </span>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white disabled:opacity-40 text-[11px] font-black text-slate-600 hover:bg-slate-100 transition-all"
                            >
                                Prev
                            </button>

                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i + 1)}
                                    className={`w-8 h-8 rounded-lg text-[11px] font-black transition-all ${
                                        currentPage === i + 1
                                            ? 'bg-indigo-700 text-white'
                                            : 'bg-white border border-slate-200 text-slate-500 hover:bg-indigo-50'
                                    }`}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white disabled:opacity-40 text-[11px] font-black text-slate-600 hover:bg-slate-100 transition-all"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

        </main>
    );
}